import styled, { DefaultTheme, css, keyframes } from "styled-components";
import { SpacingStyle, getMarginStyle, getPaddingStyle } from "core/styles/paddingMarginStyle";

type Container = { width?: string | number; height?: string | number } & SpacingStyle;

export const Container = styled.div<Container>`
  position: relative;
  border-radius: 50px;
  width: ${({ width }) => (typeof width === "string" ? width : `${width}px`)};
  height: ${({ height }) => (typeof height === "string" ? height : `${height}px`)};
  background-color: ${({ theme }) => theme?.colors?.neutral.neutral30};
  overflow: hidden;

  ${getMarginStyle}
  ${getPaddingStyle}
`;

export const Desc = styled.p`
  color: ${({ theme }) => theme?.colors?.neutral.neutral90};
  margin: 0;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.4;
  z-index: 100;
  position: relative;
`;

const widthFormatStyle = ({ theme, isCounter, value, totalCount, width }: any) => {
  const isGradient = theme?.variant === "gradient";

  if (isCounter) {
    if (value > totalCount)
      return css`
        width: 100%;
      `;
    return css`
      width: ${(value / totalCount) * 100}%;
    `;
  }

  return css`
    width: ${width}%;
    padding: ${isGradient && "0 26px"};
  `;
};

type BarStyle = {
  theme: DefaultTheme & { variant: string };
  isCounter: boolean;
  value: number;
  totalCount: number;
  width: number;
  color: string;
  initialWidth: number;
};

const backgroundFormatStyle = ({ theme, isCounter, value, totalCount, width, color }: BarStyle) => {
  const isPrimary = theme?.variant === "primary";
  const isSecondary = theme?.variant === "secondary";

  if (color) return;

  if (isCounter) {
    if (value > totalCount)
      return css`
        background: ${({ theme }) => theme?.colors?.gradient.gradient3};
      `;

    if (value <= 0)
      return css`
        border-left: 1px solid ${({ theme }) => theme?.colors?.primary.default};
      `;

    return css`
      background: ${({ theme }) => theme?.colors?.bgProgress};
    `;
  }

  if (isPrimary) {
    return css`
      background: ${({ theme }) => theme?.colors?.primary.default};
    `;
  }

  if (isSecondary) {
    return css`
      background: ${({ theme }) => theme?.colors?.secondary.default};
    `;
  }

  return css`
    background: ${width > 0 ? theme?.colors?.bgProgress : theme?.colors?.gray.gray30};
  `;
};

const progressAnimation = (initialWidth: number, width: number) => keyframes`
  from {
    width: ${initialWidth}%;
  }

  to {
    width: ${width}%;
  }
`;

type BarProps = {
  initialWidth: number;
  width: number;
};

export const Bar = styled.div<BarStyle>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ color }) => color};

  &.animated-progress {
    animation: ${({ initialWidth, width }: BarProps) => progressAnimation(initialWidth, width)} 1s
      ease-in-out;
  }

  ${widthFormatStyle}
  ${backgroundFormatStyle}
`;

export const Text = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme?.colors?.white};
`;

const minWidthFormatter = ({ value, totalCount }: any) => {
  if (value > totalCount)
    return css`
      min-width: 100%;
    `;
  if (value <= 0)
    return css`
      min-width: 21px;
    `;
  return css`
    min-width: ${(value / totalCount) * 100}%;
  `;
};

export const TextCounter = styled(Text)<{ value: number }>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
  padding-left: 9px;
  text-align: center;
  bottom: 0;
  ${minWidthFormatter}
  color: ${({ value, theme }) =>
    value <= 0 ? theme?.colors?.neutral.neutral90 : theme?.colors?.white};
`;
